import { SharedUiColorNameV2 } from '@/shared/ui/theming';
import { UiFieldComponent } from '@/ui/field/ui-field';
import { UiRadioButtonComponent } from '@/ui/radio-button/ui-radio-button';
import { ControlValueAccessorInteropDirective } from '@/utils/angular/forms/reactive-forms/reactive-forms-signals-interop.utils';
import { translate } from '@/utils/i18n/translate.reactive-helpers';
import { slugify } from '@/utils/toolkit/string/slugify.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { type AfterViewInit, Component, type ElementRef, inject, type OnInit, output, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, type Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AdvizeoBooleanControlGroupDirective } from '../../../directives/advizeo-boolean-controls-group/advizeo-boolean-control-group.directive';
import { AdvizeoBooleanControlDirective } from '../../../directives/advizeo-boolean-controls-group/advizeo-boolean-control.directive';
import { AdvizeoInputDirective } from '../../../directives/advizeo-input/advizeo-input.directive';
import type { Project, ProjectId } from '../../../interfaces/Project.interface';
import { ProjectsProvider } from '../../../providers/projects/projects.provider';
import {
    type AdvizeoModalButton,
    type AdvizeoModalComponent,
    injectAdvizeoModalInput
} from '../../advizeo-modal-spawner/advizeo-modal-spawner.component';

@UntilDestroy()
@Component({
    styleUrls: ['./project-select-modal.scss'],
    selector: 'project-select-modal',
    templateUrl: 'project-select-modal.html',
    imports: [
        UiFieldComponent,
        AdvizeoInputDirective,
        FormsModule,
        AdvizeoBooleanControlGroupDirective,
        UiRadioButtonComponent,
        AdvizeoBooleanControlDirective,
        AsyncPipe,
        TranslatePipe,
        ControlValueAccessorInteropDirective
    ]
})
export class ProjectSelectModalComponent implements AdvizeoModalComponent<null, ProjectId>, OnInit, AfterViewInit {
    private _projectsProvider = inject(ProjectsProvider);
    private _translate = inject(TranslateService);

    title = translate('COMPONENTS.PROJECT_SELECT.SELECT_A_PROJECT');
    isDismissable = signal(true);
    buttons = signal<AdvizeoModalButton[]>([
        {
            text: this._translate.instant('BUTTONS.VALIDATE'),
            color: SharedUiColorNameV2.PRIMARY,
            onClick: () => {
                this.validate();
            }
        }
    ]);

    input = injectAdvizeoModalInput<null>();
    close = output<ProjectId | undefined>();

    search$ = new BehaviorSubject<string>('');
    projects$!: Observable<Project[]>;

    selectedProjectId!: ProjectId;

    @ViewChild('searchField') searchField!: ElementRef<HTMLInputElement>;

    ngOnInit() {
        this.projects$ = combineLatest([this._projectsProvider.getUserProjects$(), this.search$]).pipe(
            map(([projects, search]) => {
                const slugifiedSearch = slugify(search)!;
                return projects.filter(project => slugify(project.name)?.includes(slugifiedSearch));
            })
        );

        this._projectsProvider
            .getUserCurrentProjectId$()
            .pipe(filter(isNotNullish), untilDestroyed(this))
            .subscribe(projectId => {
                this.selectedProjectId = projectId;
            });
    }

    ngAfterViewInit() {
        this.searchField.nativeElement.click();
    }

    validate() {
        this.close.emit(this.selectedProjectId);
    }
}
